import type { AccentLevel, Subdivision, TickLevel } from '~/utils/metronomeMeter'
import type { VoiceId } from '~/utils/metronomeVoices'
import { beatsPerBar, clampBpm, subdivisionOffsets, tickLevel } from '~/utils/metronomeMeter'
import { getVoice } from '~/utils/metronomeVoices'

/**
 * Web Audio metronome engine.
 *
 * Two things distinguish it from the old useSyncedMetronome:
 *
 * 1. Audio and visuals share one clock. The scheduler queues ticks onto the
 *    AudioContext timeline and pushes them onto a queue; a single rAF loop
 *    drains that queue as each tick actually sounds. The previous version had
 *    the scheduler and the draw loop independently re-deriving the beat from
 *    Date.now(), which is why it needed a pile of nested setTimeouts to look
 *    roughly right.
 * 2. Sync is a parameter. Off (the default) the metronome starts immediately
 *    on its own phase; on, it aligns to the server-corrected clock.
 */

export interface MetronomeEq {
  low: number
  mid: number
  high: number
}

export interface MetronomeEngineConfig {
  bpm: number
  timeSignature: string
  compound: boolean
  subdivision: Subdivision
  accents: AccentLevel[]
  voice: VoiceId
  /** 0..1 */
  volume: number
  /** Semitones, -12..+12 */
  pitch: number
  /** Per-band gain in dB, -20..+20 */
  eq: MetronomeEq
  muted: boolean
  syncEnabled: boolean
}

interface QueuedTick {
  beat: number
  slot: number
  time: number
  level: TickLevel | null
}

/** How often the scheduler wakes, and how far ahead it queues audio. */
const LOOKAHEAD_MS = 25
const SCHEDULE_AHEAD_S = 0.1

/** Small cushion so the very first tick is never scheduled in the past. */
const START_BUFFER_S = 0.06

export function useMetronomeEngine(getConfig: () => MetronomeEngineConfig) {
  const isPlaying = ref(false)
  const isPreparing = ref(false)
  const currentBeat = ref(0)
  const currentSlot = ref(0)
  /** Increments on every downbeat-of-a-beat tick; drives the dot flash. */
  const beatTick = ref(0)

  const serverClock = useServerClock()

  let ctx: AudioContext | null = null
  let voiceBus: GainNode | null = null
  let masterGain: GainNode | null = null
  let lowShelf: BiquadFilterNode | null = null
  let midPeak: BiquadFilterNode | null = null
  let highShelf: BiquadFilterNode | null = null

  let schedulerTimer: number | null = null
  let rafId: number | null = null

  const queue: QueuedTick[] = []
  let nextNoteTime = 0
  let beatStartTime = 0
  let nextBeat = 0
  let nextSlot = 0

  function ensureContext(): AudioContext | null {
    if (!import.meta.client) return null

    if (!ctx) {
      const Ctor =
        window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      if (!Ctor) return null
      ctx = new Ctor()

      // voice -> low -> mid -> high -> master -> out.
      // Routing every voice through one master gain is what makes mute
      // actually silence the click; the old engine set the gain to 1 on every
      // note and only ever changed the button's icon.
      voiceBus = ctx.createGain()
      voiceBus.gain.value = 1

      lowShelf = ctx.createBiquadFilter()
      lowShelf.type = 'lowshelf'
      lowShelf.frequency.value = 250

      midPeak = ctx.createBiquadFilter()
      midPeak.type = 'peaking'
      midPeak.frequency.value = 1000
      midPeak.Q.value = 1

      highShelf = ctx.createBiquadFilter()
      highShelf.type = 'highshelf'
      highShelf.frequency.value = 4000

      masterGain = ctx.createGain()

      voiceBus.connect(lowShelf)
      lowShelf.connect(midPeak)
      midPeak.connect(highShelf)
      highShelf.connect(masterGain)
      masterGain.connect(ctx.destination)

      applyMix()
    }

    if (ctx.state === 'suspended') {
      // Safari/iOS start suspended until a gesture; every caller is one.
      void ctx.resume()
    }

    return ctx
  }

  /** Push volume/mute/EQ from config into the graph. */
  function applyMix() {
    if (!ctx || !masterGain || !lowShelf || !midPeak || !highShelf) return
    const config = getConfig()
    const at = ctx.currentTime

    masterGain.gain.setTargetAtTime(config.muted ? 0 : Math.min(Math.max(config.volume, 0), 1), at, 0.01)
    lowShelf.gain.setTargetAtTime(config.eq.low, at, 0.01)
    midPeak.gain.setTargetAtTime(config.eq.mid, at, 0.01)
    highShelf.gain.setTargetAtTime(config.eq.high, at, 0.01)
  }

  function beatsInBar(config: MetronomeEngineConfig): number {
    return Math.max(1, beatsPerBar(config.timeSignature, config.compound))
  }

  function beatDurationOf(config: MetronomeEngineConfig): number {
    return 60 / clampBpm(config.bpm)
  }

  /**
   * Step the write head to the next tick. Config is read fresh each time, so a
   * tempo or meter change takes effect from the next tick rather than
   * restarting playback.
   */
  function advance(config: MetronomeEngineConfig) {
    const offsets = subdivisionOffsets(config.subdivision)
    const beatDuration = beatDurationOf(config)

    nextSlot++
    if (nextSlot >= offsets.length) {
      nextSlot = 0
      beatStartTime += beatDuration
      nextBeat = (nextBeat + 1) % beatsInBar(config)
    }

    nextNoteTime = beatStartTime + (offsets[nextSlot] ?? 0) * beatDuration
  }

  function scheduler() {
    if (!ctx || !voiceBus) return

    while (nextNoteTime < ctx.currentTime + SCHEDULE_AHEAD_S) {
      const config = getConfig()
      const bars = beatsInBar(config)
      const beat = nextBeat % bars
      const accent = (config.accents[beat] ?? 1) as AccentLevel
      const level = tickLevel(accent, nextSlot)

      if (level) {
        getVoice(config.voice).render(ctx, voiceBus, nextNoteTime, level, { pitch: config.pitch })
      }

      queue.push({ beat, slot: nextSlot, time: nextNoteTime, level })
      advance(config)
    }

    schedulerTimer = window.setTimeout(scheduler, LOOKAHEAD_MS)
  }

  /** Drain the queue in step with the audio, so the dots land on the click. */
  function draw() {
    if (!ctx || !isPlaying.value) return

    while (queue.length && queue[0]!.time <= ctx.currentTime) {
      const tick = queue.shift()!
      currentBeat.value = tick.beat
      currentSlot.value = tick.slot

      if (tick.slot === 0) {
        beatTick.value++
        // The first audible tick means the wait for a synced downbeat is over.
        if (isPreparing.value) isPreparing.value = false
      }
    }

    rafId = requestAnimationFrame(draw)
  }

  /**
   * Time (in AudioContext seconds) at which the shared grid's next downbeat
   * falls. Every synced device computes the same instant from the corrected
   * wall clock, so they land together regardless of when each pressed play.
   */
  function nextSharedDownbeat(config: MetronomeEngineConfig): number {
    if (!ctx) return 0

    const barMs = beatDurationOf(config) * beatsInBar(config) * 1000
    const intoBar = ((serverClock.syncedNow() % barMs) + barMs) % barMs
    const waitS = (barMs - intoBar) / 1000

    return ctx.currentTime + waitS
  }

  async function start() {
    if (isPlaying.value || isPreparing.value) return
    if (!ensureContext() || !ctx) return

    const config = getConfig()
    queue.length = 0
    nextBeat = 0
    nextSlot = 0
    currentBeat.value = 0
    currentSlot.value = 0

    if (config.syncEnabled) {
      isPreparing.value = true
      await serverClock.ensureSynced()

      // The toggle can be switched off while the handshake is in flight.
      if (!isPreparing.value) return

      beatStartTime = nextSharedDownbeat(getConfig())
    } else {
      beatStartTime = ctx.currentTime + START_BUFFER_S
    }

    nextNoteTime = beatStartTime
    isPlaying.value = true
    applyMix()
    scheduler()
    rafId = requestAnimationFrame(draw)
  }

  function stop() {
    isPlaying.value = false
    isPreparing.value = false
    queue.length = 0

    if (schedulerTimer !== null) {
      clearTimeout(schedulerTimer)
      schedulerTimer = null
    }
    if (rafId !== null) {
      cancelAnimationFrame(rafId)
      rafId = null
    }
  }

  function toggle() {
    if (isPlaying.value || isPreparing.value) {
      stop()
    } else {
      void start()
    }
  }

  /** One-shot accent tick, for previewing a voice in the sound picker. */
  function preview(voiceId?: VoiceId) {
    if (!ensureContext() || !ctx || !voiceBus) return
    const config = getConfig()
    applyMix()
    getVoice(voiceId ?? config.voice).render(ctx, voiceBus, ctx.currentTime + 0.02, 'accent', {
      pitch: config.pitch,
    })
  }

  onBeforeUnmount(() => {
    stop()
    if (ctx) {
      void ctx.close()
      ctx = null
    }
  })

  return {
    isPlaying: readonly(isPlaying),
    isPreparing: readonly(isPreparing),
    currentBeat: readonly(currentBeat),
    currentSlot: readonly(currentSlot),
    beatTick: readonly(beatTick),
    clockStatus: serverClock.status,
    clockAccuracyMs: serverClock.accuracyMs,
    start,
    stop,
    toggle,
    preview,
    applyMix,
  }
}
